import quarterOfYear from "dayjs/plugin/quarterOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useMemo } from "react";
import dayjs from "dayjs";

import { Intervals } from "../../../../api/api-hooks/records/useQueriedRecords";
import { ReusableViewIDs, View } from "../../../../types/PageInterfaces";
import NoCardsLayouts from "../PageViewCards/NoCardsLayouts";
import { Prefill } from "../../../../types/GlobalInterfaces";
import { Module } from "../../../../types/TenantInterfaces";
import { SharedViewModes } from "../../PageViewRenderer";
import useGroupBySchema from "./useGroupBySchema";
import { BoardLayout } from "./PageViewBoard";
import Board from "./Board";

dayjs.extend(quarterOfYear);
dayjs.extend(advancedFormat);

export interface TimelineLayout extends BoardLayout {
  TimeFrame: Intervals;
}

interface Props {
  currentModule: Module;
  foundLayout: TimelineLayout;
  query: any;
  records: any[];
  recordsLoading: boolean;
  props: any;
  selectedRecords: { [key: number]: boolean };
  setSelectedRecords: (newObj: object) => void;
  setSelectedPrefill: React.Dispatch<React.SetStateAction<Prefill | null>>;
  toggleCreateRecordsDrawer: () => void;
  view: View;
  parentViewID?: string;
  viewMode: SharedViewModes;
  onOpenRecordName: any;
  reusableViews?: ReusableViewIDs;
}

const emptyLayouts: [] = [];

/**
 * PageViewTimeline - Specialized Kanban Board for handling date/time fields.
 *                    Shares a lot of logic with PageViewBoard - See imports at the top.
 */
export default function PageViewTimeline({
  currentModule,
  query,
  foundLayout,
  records,
  recordsLoading,
  selectedRecords,
  setSelectedRecords,
  setSelectedPrefill,
  toggleCreateRecordsDrawer,
  view,
  parentViewID,
  viewMode,
  onOpenRecordName,
  reusableViews,
}: Props) {
  const timelineLayouts = view?.TimelineLayouts ?? emptyLayouts;
  const groupBy = foundLayout?.GroupBy;
  const interval = foundLayout?.TimeFrame;

  const groupByFieldSchema = useGroupBySchema(currentModule?.Fields, groupBy);

  const groups = useMemo(() => {
    return records?.flatMap((r) => {
      if (!r?.GroupBy?.[groupBy]) {
        return !!foundLayout?.EmptyGroup
          ? [
              {
                Name: `No ${groupBy} Date`,
                Value: null,
              },
            ]
          : [];
      }

      const name = GetDateName(r?.GroupBy?.[groupBy], interval);

      const option = { Name: name, Value: r?.GroupBy?.[groupBy] };

      return [option];
    });
  }, [records, groupBy, interval, foundLayout?.EmptyGroup]);

  if (!timelineLayouts?.length) {
    return (
      <NoCardsLayouts
        currentModule={currentModule}
        viewID={view?._id ?? ""}
        parentViewID={parentViewID}
        viewMode={viewMode}
      />
    );
  }

  return (
    <Board
      onOpenRecordName={onOpenRecordName}
      records={records}
      recordsLoading={recordsLoading}
      currentModule={currentModule}
      selectedRecords={selectedRecords}
      setSelectedRecords={setSelectedRecords}
      groupBy={groupBy}
      groups={groups}
      loading={recordsLoading}
      query={query}
      groupByFieldSchema={groupByFieldSchema}
      setSelectedPrefill={setSelectedPrefill}
      toggleCreateRecordsDrawer={toggleCreateRecordsDrawer}
      view={view}
      reusableViews={reusableViews}
    />
  );
}

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function GetDateName(dateString: string, interval: Intervals) {
  if (interval === "day") {
    return GetDay(dateString);
  }
  if (interval === "week") {
    return GetWeekDateRange(dateString);
  }
  if (interval === "year") {
    return GetYear(dateString);
  }
  if (interval === "month") {
    const year = GetYear(dateString);
    const month = GetMonth(dateString);

    return `${month} ${year}`;
  }
  if (interval === "quarter") {
    const year = GetYear(dateString);
    const month = GetMonth(dateString);

    if (month === "January") return `${year} Q1`;
    if (month === "April") return `${year} Q2`;
    if (month === "July") return `${year} Q3`;
    if (month === "October") return `${year} Q4`;
  }
}

function GetYear(dateString: string) {
  return new Date(dateString).getUTCFullYear();
}

function GetMonth(dateString: string) {
  const monthIndex = new Date(dateString).getUTCMonth();
  return monthNames[monthIndex];
}

function GetDay(isoString: string) {
  return dayjs(isoString).format("MMMM Do YYYY");
}

function GetWeekDateRange(isoString: string) {
  const start = dayjs(isoString).format("MMMM Do");
  const end = dayjs(isoString).endOf("day").add(7, "day").format("MMMM Do");

  return `${start} - ${end}`;
}
